import { html, nothing, type TemplateResult } from 'lit-html';
import { PkOverlayBase } from '../_internal/pk-overlay-base.js';
import styles from './pk-modal.css?inline';

/**
 * `<pk-modal>` — general-purpose focus-trapped dialog. v0.4
 *
 * Attributes:
 *   `open`            — boolean; shows/hides the modal.
 *   `title`           — dialog heading text.
 *   `variant`         — enum: `default` (default) | `danger` | `success`.
 *                        `danger` adds a warning glyph + danger border-top styling.
 *                        `success` adds a check glyph.
 *   `size`            — enum: `small` (360px) | `medium` (520px, default) | `large` (720px).
 *   `dismissible`     — enum: `none` | `escape` | `backdrop` (default; covers escape + backdrop click).
 *                        Back-compat: `"false"` maps to `none`; `"true"` or absent maps to `backdrop`.
 *   `return-focus-to` — id of element to focus on close. If absent, returns to previously-active element.
 *
 * Slots:
 *   _default_   — dialog body content (legacy, remains for back-compat).
 *   `body`      — preferred name for body content.
 *   `primary`   — primary action button layout slot.
 *   `secondary` — cancel/dismiss button layout slot.
 *   `details`   — optional collapsed disclosure for technical info.
 *   `footer`    — legacy footer slot (back-compat; used when primary/secondary absent).
 *
 * Events:
 *   `pk-open`        — fires when the modal becomes visible (no detail).
 *   `pk-cancel`      — fires when Escape, backdrop, or close-X dismisses the modal (no detail).
 *   `pk-close`       — fires after pk-cancel. detail: { reason: 'cancel' }.
 *   `pk-modal-close` — legacy event, still fired for back-compat (no detail).
 *
 * Consumer-fired event (documented here, NOT fired by pk-modal itself):
 *   `pk-confirm`     — consumer fires from the primary slot's button. pk-modal does not intercept clicks.
 */
export class PkModal extends PkOverlayBase {
    static readonly tagName = 'pk-modal';

    static get observedAttributes(): string[] {
        return ['open', 'title', 'dismissible', 'variant', 'size', 'return-focus-to'];
    }

    private readonly _bodyId: string;

    constructor() {
        super();
        this._bodyId = `pk-modal-body-${this._uid}`;
        this._root.addEventListener('slotchange', (e) => {
            if (e.target instanceof HTMLSlotElement) PkModal._markAssigned(e.target);
        });
    }

    /**
     * The footer CSS hides a slot until it carries `assigned` (so an empty footer takes no space). Set it from the
     * slot's real assigned nodes — without this every primary / secondary button stayed `display: none`.
     */
    private static _markAssigned(slot: HTMLSlotElement): void {
        const has = slot.assignedNodes({ flatten: true })
            .some((n) => n.nodeType === Node.ELEMENT_NODE || (n.textContent ?? '').trim() !== '');
        slot.toggleAttribute('assigned', has);
    }

    protected override _doRender(): void {
        super._doRender();
        // A reopened dialog renders fresh slot elements; mark them now rather than waiting for slotchange.
        for (const slot of Array.from(this._root.querySelectorAll('.footer slot'))) {
            PkModal._markAssigned(slot as HTMLSlotElement);
        }
    }

    protected get _idPrefix(): string {
        return 'pk-modal';
    }

    // ── Close ────────────────────────────────────────────────────────────────

    /** Legacy `pk-modal-close` event — keep for back-compat. */
    protected override _onAfterClose(): void {
        this.dispatchEvent(new CustomEvent('pk-modal-close', {
            bubbles: true,
            composed: true,
        }));
    }

    // ── Template ─────────────────────────────────────────────────────────────

    private _variantGlyph(variant: string): TemplateResult | typeof nothing {
        if (variant === 'danger') {
            return html`<span class="title-glyph title-glyph--danger" aria-hidden="true">&#x26A0;&#xFE0F;</span>`;
        }
        if (variant === 'success') {
            return html`<span class="title-glyph title-glyph--success" aria-hidden="true">&#x2714;&#xFE0F;</span>`;
        }
        return nothing;
    }

    protected override template(): TemplateResult {
        const isOpen    = this.hasAttribute('open');
        const title     = this.getAttribute('title') ?? '';
        const variant   = this.getAttribute('variant') ?? 'default';
        const size      = this.getAttribute('size') ?? 'medium';
        const showClose = this._isDismissible();

        if (!isOpen) {
            return html`<style>${styles}</style>`;
        }

        return html`
            <style>${styles}</style>
            <div class="backdrop"
                role="presentation"
                @click=${(e: MouseEvent) => this._onBackdropClick(e)}>
                <div class="dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-label=${title || 'Dialog'}
                    aria-labelledby=${this._titleId}
                    aria-describedby=${this._bodyId}
                    data-variant=${variant}
                    data-size=${size}
                    @keydown=${(e: KeyboardEvent) => this._handleTabKey(e)}>

                    <div class="header">
                        ${this._variantGlyph(variant)}
                        <h2 class="title" id=${this._titleId}>${title}</h2>
                        ${showClose ? html`
                            <button class="close-btn"
                                type="button"
                                aria-label="Close dialog"
                                @click=${() => this._close()}>
                                &#x2715;
                            </button>
                        ` : nothing}
                    </div>

                    <div class="body" id=${this._bodyId}>
                        <slot name="body"></slot>
                        <slot></slot>
                    </div>

                    <details class="details">
                        <summary>Show details</summary>
                        <slot name="details"></slot>
                    </details>

                    <div class="footer">
                        <slot name="secondary"></slot>
                        <slot name="primary"></slot>
                        <slot name="footer"></slot>
                    </div>
                </div>
            </div>
        `;
    }
}

if (!customElements.get(PkModal.tagName)) {
    customElements.define(PkModal.tagName, PkModal);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-modal': PkModal;
    }
}
